/**
 * ConfirmCard — Sprint C single-tap confirmation for known wellness leads.
 *
 * Used by FunnelPage when `?source_lead_id` resolves to a real Wellness
 * Gateway lead. We greet the user by name, surface the aligned next step
 * + booking guidance, and offer one primary "Confirm & continue" CTA.
 *
 * Identity is NEVER re-collected here. If the lead lookup fails, the
 * caller renders the legacy form fallback instead.
 */
import { useState } from "react";
import { formatApiError } from "../../lib/formatError";

export default function ConfirmCard({
  leadFirstName,
  leadEmail,
  funnelKey,
  funnelEyebrow,
  funnelHeadline,
  nextStepCopy,
  bookingGuidance,
  primaryCtaLabel = "Confirm & continue →",
  onConfirm,                 // async ({ note }) => { id, expectation_copy }
  onSwitchToLegacyForm,      // user explicitly opts out (rare)
  testidPrefix = "confirm-card",
}) {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);  // { expectation_copy }
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await onConfirm({ note: note.trim() });
      setSubmitted({
        expectation_copy:
          result?.expectation_copy ||
          "We'll reach out within 1 business day with your aligned next step.",
      });
    } catch (err) {
      setError(formatApiError(err, "We couldn't confirm just now. Please try again."));
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <section
        className="funnel-confirm-card funnel-confirm-card-success"
        data-testid={`${testidPrefix}-success`}
      >
        <div className="funnel-confirm-eyebrow">A note from KISSED</div>
        <h2 className="funnel-confirm-heading" data-testid={`${testidPrefix}-success-heading`}>
          You're in, {leadFirstName}.
        </h2>
        <p className="funnel-confirm-expectation" data-testid={`${testidPrefix}-expectation`}>
          {submitted.expectation_copy}
        </p>
        <p className="funnel-confirm-note">
          Check your inbox at <strong>{leadEmail}</strong> — including your spam folder,
          just in case.
        </p>
      </section>
    );
  }

  return (
    <section className="funnel-confirm-card" data-testid={testidPrefix}>
      <div className="funnel-confirm-eyebrow" data-testid={`${testidPrefix}-eyebrow`}>
        {funnelEyebrow || "Your aligned next step"}
      </div>
      <h2 className="funnel-confirm-heading" data-testid={`${testidPrefix}-heading`}>
        Ready to take this step, {leadFirstName}?
      </h2>
      {funnelHeadline && (
        <p className="funnel-confirm-subhead" data-testid={`${testidPrefix}-subhead`}>
          {funnelHeadline}
        </p>
      )}

      {nextStepCopy && (
        <div className="funnel-confirm-block" data-testid={`${testidPrefix}-next-step`}>
          <span className="funnel-confirm-block-label">Your aligned next step</span>
          <p>{nextStepCopy}</p>
        </div>
      )}

      {bookingGuidance && (
        <div className="funnel-confirm-block" data-testid={`${testidPrefix}-booking`}>
          <span className="funnel-confirm-block-label">What this looks like</span>
          <p>{bookingGuidance}</p>
        </div>
      )}

      <form onSubmit={submit} className="funnel-confirm-form">
        <p className="funnel-confirm-identity" data-testid={`${testidPrefix}-identity-row`}>
          We'll use <strong>{leadEmail}</strong> — the email you shared earlier — to
          send your confirmation. <span className="funnel-confirm-identity-divider">·</span>{" "}
          <button
            type="button"
            className="funnel-confirm-switch-link"
            data-testid={`${testidPrefix}-switch-link`}
            onClick={onSwitchToLegacyForm}
          >
            Use a different email
          </button>
        </p>

        <label
          className="funnel-confirm-note-label"
          htmlFor={`${testidPrefix}-note`}
        >
          Anything we should know? <span className="funnel-confirm-optional">(optional)</span>
        </label>
        <textarea
          id={`${testidPrefix}-note`}
          data-testid={`${testidPrefix}-note`}
          className="funnel-confirm-textarea"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add any context that helps us reach out well…"
        />

        {error && (
          <p className="funnel-confirm-error" role="alert" data-testid={`${testidPrefix}-error`}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid={`${testidPrefix}-button`}
          className="funnel-confirm-button"
        >
          {submitting ? "Confirming…" : primaryCtaLabel}
        </button>
        <p className="funnel-confirm-disclaimer">
          One step. No new forms. We've already got your details.
        </p>
      </form>
    </section>
  );
}
